import type { AlertDialog } from '@base-ui/react';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { Contact } from '~/api/clients/types';

import { createAppColumnHelper } from '~/components';
import { ColumnIds } from '~/constants/table';

import { ContactEmailsCell } from './ContactsTableCells/ContactEmailsCell';
import { ContactMobileCell } from './ContactsTableCells/ContactMobileCell';
import { ContactNameCell } from './ContactsTableCells/ContactNameCell';
import { ContactOptionsCell } from './ContactsTableCells/ContactOptionsCell';
import { ContactPhoneCell } from './ContactsTableCells/ContactPhoneCell';

export type DialogHandles = Record<'deleteContactHandle', AlertDialog.Handle<Contact>>;

const columnHelper = createAppColumnHelper<Contact>();

export const useContactsTableColumns = ({ dialogHandles }: { dialogHandles: DialogHandles }) => {
  const { t } = useTranslation(['clients']);

  return useMemo(
    () =>
      columnHelper.columns([
        // Name
        columnHelper.accessor('fullName', {
          id: ColumnIds.contactsName,
          header: t('clients:detail.contacts.columns.name'),
          cell: ({ getValue }) => <ContactNameCell name={getValue()} />,
          meta: {
            styles: { size: '1fr', minSize: 150 },
            isResizable: true,
          },
        }),

        // Emails
        columnHelper.accessor(({ emails }) => emails.map(({ email }) => email).join(' '), {
          id: ColumnIds.contactsEmail,
          header: t('clients:detail.contacts.columns.email'),
          cell: ({ row: { original: contact } }) => <ContactEmailsCell contact={contact} />,
          meta: {
            styles: { size: '1fr', minSize: 150 },
            isResizable: true,
          },
        }),

        // Phone number
        columnHelper.accessor('phoneNumber', {
          id: ColumnIds.contactsPhoneNumber,
          header: t('clients:detail.contacts.columns.phoneNumber'),
          cell: ({ getValue }) => <ContactPhoneCell phoneNumber={getValue()} />,
          meta: {
            styles: { size: '1fr', minSize: 100 },
            isResizable: true,
          },
        }),

        // Options
        columnHelper.display({
          id: ColumnIds.options,
          cell: ({ row: { original: contact } }) => <ContactOptionsCell contact={contact} dialogHandles={dialogHandles} />,
          meta: {
            styles: { size: 'max-content', minSize: 40 },
          },
        }),

        // Mobile
        columnHelper.display({
          id: ColumnIds.contactsMobile,
          cell: ({ row: { original: contact } }) => <ContactMobileCell contact={contact} dialogHandles={dialogHandles} />,
          meta: { mobile: true },
        }),
      ]),
    [t, dialogHandles],
  );
};
